import { useState, useEffect, useCallback, useRef } from 'react';
import { requestService } from '../services/requestService';
import { transformRequestForDisplay } from '../utils/transformers';
import { parseApiError } from '../utils/errorUtils';

// Minimum time to show loading skeleton (prevents flash)
const MIN_LOADING_DURATION = 400;

export function useRequests(initialParams, sortBy, sortDir) {
  // Use ref to ensure stable params reference across renders
  const paramsRef = useRef(initialParams || {});

  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ hasMore: false, nextKey: null });

  // Core fetch logic shared by initial load and silent refetch
  const fetchAndTransform = useCallback(async (params = {}, signal) => {
    const sortParams = {};
    if (sortBy) sortParams.sort_by = sortBy;
    if (sortDir) sortParams.sort_dir = sortDir;
    const response = await requestService.list({ ...paramsRef.current, ...sortParams, ...params, signal });

    if (!response?.data?.requests || !Array.isArray(response.data.requests)) {
      throw new Error('Invalid response format from requests API');
    }

    const transformed = response.data.requests
      .map((request, index) => {
        try {
          return transformRequestForDisplay(request);
        } catch (err) {
          console.error(`[useRequests] Transform failed for request ${index}:`, err);
          return null;
        }
      })
      .filter(Boolean);

    return {
      requests: transformed,
      pagination: {
        hasMore: response.data.pagination?.has_more || false,
        nextKey: response.data.pagination?.next_key || null,
      },
    };
  }, [sortBy, sortDir]);

  // Full fetch with loading state — used for initial load and explicit refetch
  const fetchRequests = useCallback(async (params = {}, signal) => {
    setIsLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      const result = await fetchAndTransform(params, signal);

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_LOADING_DURATION) {
        await new Promise(resolve => setTimeout(resolve, MIN_LOADING_DURATION - elapsed));
      }

      setRequests(result.requests);
      setPagination(result.pagination);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }
      console.error('[useRequests] Fetch failed:', err);
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, [fetchAndTransform]);

  // Silent refetch — no loading state, only updates rows that changed
  const silentRefetch = useCallback(async () => {
    try {
      const result = await fetchAndTransform({});

      setRequests(prev => {
        // Build a map of new data keyed by ID
        const newMap = new Map(result.requests.map(r => [r.id, r]));
        const prevMap = new Map(prev.map(r => [r.id, r]));

        // Check if anything actually changed. lastSeenStatus is included so a
        // server-side markSeen (which updates the per-user view state without
        // touching status/updatedAt) still re-renders the row to clear the
        // NEW badge.
        if (newMap.size === prevMap.size) {
          let changed = false;
          for (const [id, newReq] of newMap) {
            const prevReq = prevMap.get(id);
            if (!prevReq ||
                prevReq.updatedAt !== newReq.updatedAt ||
                prevReq.status !== newReq.status ||
                prevReq.lastSeenStatus !== newReq.lastSeenStatus) {
              changed = true;
              break;
            }
          }
          if (!changed) return prev; // No state update, no re-render
        }

        // Merge: keep unchanged row references, replace only changed ones
        return result.requests.map(newReq => {
          const prevReq = prevMap.get(newReq.id);
          if (prevReq &&
              prevReq.updatedAt === newReq.updatedAt &&
              prevReq.status === newReq.status &&
              prevReq.lastSeenStatus === newReq.lastSeenStatus) {
            return prevReq; // Same reference — React skips re-render for this row
          }
          return newReq;
        });
      });

      setPagination(result.pagination);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('[useRequests] Silent refetch failed:', err);
      }
    }
  }, [fetchAndTransform]);

  // Initial fetch with cleanup
  // Small delay to ensure React renders loading state before fetch starts
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      fetchRequests({}, controller.signal);
    }, 10); // Small delay to allow loading state to render
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchRequests]);

  const createRequest = useCallback(async (data) => {
    const response = await requestService.create(data);
    const newRequest = response.data;

    // Optimistically add to local state
    const transformed = transformRequestForDisplay(newRequest);
    setRequests(prev => [...prev, transformed]);

    // Refresh list in background
    const controller = new AbortController();
    fetchRequests({}, controller.signal).catch(() => {});

    return newRequest;
  }, [fetchRequests]);

  const updateRequest = useCallback(async (id, data) => {
    const response = await requestService.update(id, data);

    // Update in local state
    const transformed = transformRequestForDisplay(response.data);
    setRequests(prev => prev.map(r => r.id === id ? transformed : r));

    return response.data;
  }, []);

  const deleteRequest = useCallback(async (id) => {
    await requestService.delete(id);
    setRequests(prev => prev.filter(r => r.id !== id));
  }, []);

  const updateStatus = useCallback(async (id, status) => {
    const response = await requestService.updateStatus(id, status);
    const transformed = transformRequestForDisplay(response.data);
    setRequests(prev => prev.map(r => r.id === id ? transformed : r));
    return response.data;
  }, []);

  const assignCarrier = useCallback(async (id, carrierId) => {
    const response = await requestService.assignCarrier(id, carrierId);
    const transformed = transformRequestForDisplay(response.data);
    setRequests(prev => prev.map(r => r.id === id ? transformed : r));
    return response.data;
  }, []);

  const unassignCarrier = useCallback(async (id) => {
    const response = await requestService.unassignCarrier(id);
    const transformed = transformRequestForDisplay(response.data);
    setRequests(prev => prev.map(r => r.id === id ? transformed : r));
    return response.data;
  }, []);

  const updateQuote = useCallback(async (id, quoteData) => {
    const response = await requestService.updateQuote(id, quoteData);
    const transformed = transformRequestForDisplay(response.data);
    setRequests(prev => prev.map(r => r.id === id ? transformed : r));
    return response.data;
  }, []);

  const loadMore = useCallback(async () => {
    if (!pagination.nextKey || isLoading) return;

    const controller = new AbortController();
    try {
      // Must use the same sort params as the initial fetch so the backend
      // queries the same index — otherwise the cursor's keys (e.g. GSI sort
      // key) won't match the new query and DynamoDB rejects it as invalid.
      const result = await fetchAndTransform(
        { cursor: pagination.nextKey },
        controller.signal,
      );

      setRequests(prev => [...prev, ...result.requests]);
      setPagination(result.pagination);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setError(parseApiError(err));
      }
    }
  }, [fetchAndTransform, pagination.nextKey, isLoading]);

  // Poll every 2s for up to 60s after AI processing starts, stopping early
  // once the target request status leaves "in_progress" (i.e., becomes "extracted" or "failed").
  // Returns a promise that resolves with the final request data.
  const pollForAIUpdate = useCallback((requestId) => {
    const INTERVAL_MS = 2000;
    const MAX_ATTEMPTS = 30; // 30 × 2s = 60s
    let attempts = 0;
    let timeoutId;
    let cancelled = false;

    const isDone = (req) =>
      req && req.rawStatus !== 'in_progress' && req.rawStatus !== 'pending';

    const promise = new Promise((resolve) => {
      const poll = async () => {
        if (cancelled) return;
        attempts++;
        try {
          const response = await requestService.getById(requestId);
          const target = transformRequestForDisplay(response.data);

          if (target) {
            setRequests(prev => {
              const exists = prev.some(r => r.id === requestId);
              if (exists) {
                return prev.map(r => r.id === requestId ? target : r);
              }
              return [...prev, target];
            });
          }

          if (isDone(target) || attempts >= MAX_ATTEMPTS) {
            resolve(target);
          } else {
            timeoutId = setTimeout(poll, INTERVAL_MS);
          }
        } catch (err) {
          if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
            resolve(null);
          }
        }
      };

      timeoutId = setTimeout(poll, INTERVAL_MS);
    });

    promise.cancel = () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };

    return promise;
  }, []);

  // markSeen optimistically clears the NEW badge for a single request by
  // setting its local lastSeenStatus to the current rawStatus, then fires the
  // backend POST in the background. We don't wait for the round-trip — the
  // backend write is idempotent and best-effort, and the next silentRefetch
  // reconciles. Without this optimistic update the badge would persist on
  // local state for up to 15s (until the next silentRefetch) after the user
  // collapses the row.
  const markSeen = useCallback(async (id) => {
    setRequests(prev => prev.map(r => {
      if (r.id !== id) return r;
      if (r.lastSeenStatus === r.rawStatus) return r; // Already seen at this status
      return { ...r, lastSeenStatus: r.rawStatus, lastSeenAt: new Date().toISOString() };
    }));
    try {
      await requestService.markSeen(id);
    } catch {
      // Best-effort: silent failure means the badge may reappear on next refetch.
    }
  }, []);

  const confirmSend = useCallback(async (id, rfqText) => {
    const response = await requestService.confirmSend(id, rfqText);
    const transformed = transformRequestForDisplay(response.data);
    setRequests(prev => prev.map(r => r.id === id ? transformed : r));
    return response.data;
  }, []);

  const extractRFQ = useCallback(async (rfqText, carrierTypes) => {
    const response = await requestService.extractRFQ(rfqText, carrierTypes);
    return response.data;
  }, []);

  const validateRFQ = useCallback(async (rfqText) => {
    const response = await requestService.validateRFQ(rfqText);
    return response.data;
  }, []);

  const getValidationStatus = useCallback(async (validationId) => {
    const response = await requestService.getValidationStatus(validationId);
    return response.data;
  }, []);

  const submitRFQ = useCallback(async (rfqText, carrierId, extraction, validationId) => {
    const response = await requestService.submitRFQ(rfqText, carrierId, extraction, validationId);
    const newRequest = response.data;
    const transformed = transformRequestForDisplay(newRequest);
    setRequests(prev => [...prev, transformed]);
    return newRequest;
  }, []);

  return {
    requests,
    isLoading,
    error,
    pagination,
    refetch: () => fetchRequests({}),
    silentRefetch,
    createRequest,
    updateRequest,
    deleteRequest,
    updateStatus,
    assignCarrier,
    unassignCarrier,
    updateQuote,
    loadMore,
    pollForAIUpdate,
    confirmSend,
    extractRFQ,
    validateRFQ,
    getValidationStatus,
    submitRFQ,
    markSeen,
  };
}
